/**
 * Client for /ws/events, the edge event router (#186 §6.3).
 *
 * One socket per page for everything the page shows: VMs (local and
 * delegated, already merged by the edge), host reachability, host metrics,
 * tasks. The client sends a hello (resuming from the last epoch/seq after a
 * reconnect), applies the snapshot or replay, then live set/patch/remove
 * events, and hands each change to the page's handlers with the entity's
 * full current state.
 */

import { api } from './client.js';

export type EventKind = 'vm' | 'host' | 'host_metrics' | 'log' | 'task' | 'notice';

/** One envelope from the server (§3). */
interface Envelope {
  v: number;
  seq: number;
  ts: number;
  host: string;
  kind: EventKind;
  op: 'set' | 'patch' | 'remove' | 'append';
  key: string;
  data?: Record<string, unknown>;
}

export interface EventHandlers {
  /** A VM's full current state after a change, and the fields that change
   *  removed (a patch's nulls, or keys a fresh `set` no longer carries). */
  onVm?: (uuid: string, state: Record<string, unknown>, removed: string[]) => void;
  /** A VM is gone. */
  onVmRemoved?: (uuid: string) => void;
  /** A peer host's reachability as seen by this edge (live/stale/unreachable/...). */
  onHost?: (host: string, state: Record<string, unknown>) => void;
  /** A host's metrics; isLocal when `host` is the edge this page talks to. */
  onHostMetrics?: (host: string, metrics: Record<string, unknown>, isLocal: boolean) => void;
  onTask?: (taskId: string, task: Record<string, unknown> | null) => void;
  onNotice?: (host: string, notice: Record<string, unknown>) => void;
  /** The snapshot or replay is complete (also after each resync). */
  onReady?: () => void;
}

const RECONNECT_DELAY_INITIAL = 1000;
const RECONNECT_DELAY_MAX = 30000;

export interface EventsConnection {
  close(): void;
}

/**
 * Open /ws/events for `kinds` and keep it open (reconnecting with backoff,
 * resuming where it left off). Returns a handle to close it.
 */
export function connectEvents(kinds: EventKind[], handlers: EventHandlers): EventsConnection {
  const entities = new Map<string, Record<string, unknown>>();   // "kind|key|host"
  let ws: WebSocket | null = null;
  let stopped = false;
  let epoch: string | null = null;
  let localHost: string | null = null;   // from the snapshot/replay header
  let lastSeq = 0;
  let inSnapshot = false;
  let snapshotKeys = new Set<string>();
  let reconnectDelay = RECONNECT_DELAY_INITIAL;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const entityKey = (e: Envelope) =>
    e.kind === 'host' || e.kind === 'host_metrics' ? `${e.kind}||${e.host}` : `${e.kind}|${e.key}|`;

  function deliver(kind: EventKind, key: string, host: string,
                   state: Record<string, unknown> | null, removed: string[] = []): void {
    if (kind === 'vm') {
      if (state) handlers.onVm?.(key, state, removed);
      else handlers.onVmRemoved?.(key);
    } else if (kind === 'host') {
      if (state) handlers.onHost?.(host, state);
    } else if (kind === 'host_metrics') {
      if (state) handlers.onHostMetrics?.(host, state, host === localHost);
    } else if (kind === 'task') {
      handlers.onTask?.(key, state);
    }
  }

  function apply(e: Envelope): void {
    if (e.seq <= lastSeq && !inSnapshot) return;   // already applied
    if (!inSnapshot) lastSeq = e.seq;
    if (e.kind === 'notice') {
      if (e.data) handlers.onNotice?.(e.host, e.data);
      return;
    }
    if (e.kind === 'log') return;   // history is paged from the REST API
    const k = entityKey(e);
    if (e.op === 'remove') {
      entities.delete(k);
      deliver(e.kind, e.key, e.host, null);
      return;
    }
    let state = entities.get(k);
    const removed: string[] = [];
    if (e.op === 'set' || !state) {
      const next: Record<string, unknown> = { ...(e.data ?? {}) };
      if (state) for (const f of Object.keys(state)) if (!(f in next)) removed.push(f);
      state = next;
    } else {
      for (const [f, v] of Object.entries(e.data ?? {})) {
        if (v === null) {   // a null in a patch removes the field
          delete state[f];
          removed.push(f);
        } else {
          state[f] = v;
        }
      }
    }
    // The origin host travels in the envelope; keep it with the state.
    if (e.kind === 'vm') state['origin_host'] = e.host;
    entities.set(k, state);
    if (inSnapshot) snapshotKeys.add(k);
    deliver(e.kind, e.key, e.host, state, removed.filter(f => f !== 'origin_host'));
  }

  function onMessage(raw: string): void {
    let msg: Record<string, unknown>;
    try {
      msg = JSON.parse(raw);
    } catch {
      return;
    }
    switch (msg['type']) {
      case 'snapshot':
        epoch = msg['epoch'] as string;
        localHost = msg['host'] as string;
        lastSeq = msg['seq'] as number;
        inSnapshot = true;
        snapshotKeys = new Set();
        return;
      case 'replay':
        localHost = msg['host'] as string;
        inSnapshot = false;
        return;
      case 'ready':
        if (inSnapshot) {
          // Anything we held that the snapshot didn't mention is gone.
          for (const k of [...entities.keys()]) {
            if (snapshotKeys.has(k)) continue;
            const [kind, key, host] = k.split('|') as [EventKind, string, string];
            entities.delete(k);
            deliver(kind, key, host, null);
          }
          inSnapshot = false;
        }
        lastSeq = Math.max(lastSeq, msg['seq'] as number);
        handlers.onReady?.();
        return;
      case 'resync':
        // The server dropped events for us (we fell behind); a fresh
        // snapshot follows.
        return;
      case 'heartbeat':
      case 'pong':
        return;
      case 'error':
        console.warn('events: server error:', msg['message']);
        return;
    }
    if ('kind' in msg) apply(msg as unknown as Envelope);
  }

  function scheduleReconnect(): void {
    if (stopped) return;
    timer = setTimeout(() => { timer = null; open(); }, reconnectDelay);
    reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_DELAY_MAX);
  }

  function open(): void {
    if (stopped || ws) return;
    const socket = new WebSocket(api.buildWsUrl('/ws/events'));
    ws = socket;
    let opened = false;
    socket.onopen = () => {
      opened = true;
      reconnectDelay = RECONNECT_DELAY_INITIAL;
      const hello: Record<string, unknown> = { type: 'hello', v: 1, kinds };
      if (epoch) hello['resume'] = { epoch, seq: lastSeq };
      socket.send(JSON.stringify(hello));
    };
    socket.onmessage = (ev) => onMessage(ev.data as string);
    socket.onclose = () => {
      ws = null;
      // Cut off mid-snapshot: what we hold is partial, so resuming from the
      // snapshot's seq would skip the entities we never got. Start over.
      if (inSnapshot) epoch = null;
      inSnapshot = false;
      if (stopped) return;
      if (!opened) {
        // The upgrade failed: the session may have expired.
        api.verifySession().then(valid => { if (valid) scheduleReconnect(); });
      } else {
        scheduleReconnect();
      }
    };
    socket.onerror = () => { /* onclose follows */ };
  }

  open();
  return {
    close(): void {
      stopped = true;
      if (timer) clearTimeout(timer);
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING))
        ws.close();
      ws = null;
    },
  };
}
